import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  getGetStoresQueryOptions,
  useRequestOtp,
  useVerifyOtp,
  type requestOtpResponse200,
  type verifyOtpResponse200,
} from "@/api";
import { OtpVerifyTemplate } from "@/components/Templates";
import { useOtpTimer } from "@/hooks/useOtpTimer";
import { getErrorMessage } from "@/lib/api/errors";
import { consumePostLoginRedirect } from "@/lib/auth/postLoginRedirect";
import { otpVerifySchema, type OtpVerifyFormValues } from "@/lib/validation/auth.schemas";
import { useAuthStore } from "@/stores/auth.store";

export function OtpVerifyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { phone, resendAfter } = useSearch({ from: "/_auth/otp/verify" });
  const setAuth = useAuthStore((state) => state.setAuth);
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string>();

  const { seconds, isExpired, reset } = useOtpTimer(resendAfter);

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<OtpVerifyFormValues>({
    resolver: zodResolver(otpVerifySchema),
    defaultValues: { code: "" },
  });

  const { mutate: verifyOtp, isPending } = useVerifyOtp<verifyOtpResponse200>({
    mutation: {
      onSuccess: async (res) => {
        if (res.status !== 200) return;
        setAuth({
          accessToken: res.data.data.access_token,
          user: { username: phone },
        });
        await queryClient.prefetchQuery(getGetStoresQueryOptions());
        navigate({ to: consumePostLoginRedirect() ?? "/" });
      },
      onError: (error) => {
        setErrorMessage(getErrorMessage(error, "کد تأیید نامعتبر است"));
      },
    },
  });

  const { mutate: resendOtp, isPending: isResending } = useRequestOtp<requestOtpResponse200>({
    mutation: {
      onSuccess: (res) => {
        if (res.status !== 200) return;
        reset(res.data.data.resend_after);
        setErrorMessage(undefined);
      },
      onError: (error) => setErrorMessage(getErrorMessage(error, "ارسال دوباره کد انجام نشد")),
    },
  });

  const handleCodeChange = (nextCode: string) => {
    setCode(nextCode);
    setValue("code", nextCode, { shouldValidate: true });
    setErrorMessage(undefined);
  };

  const onSubmit = handleSubmit((data) => {
    verifyOtp({ data: { phone, code: data.code } });
  });

  const handleResend = () => {
    if (!isExpired || isResending) return;
    resendOtp({ data: { phone } });
  };

  return (
    <OtpVerifyTemplate
      phone={phone}
      code={code}
      onCodeChange={handleCodeChange}
      onSubmit={onSubmit}
      onResend={handleResend}
      seconds={seconds}
      isExpired={isExpired}
      isPending={isPending}
      isResending={isResending}
      errorMessage={errorMessage ?? errors.code?.message}
    />
  );
}
